import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { NotificationResponseForApprovals } from '../models/NotificationResponseForApprovals';
import { DownloadJobStatus } from '../models/download-job';
import { CommonService } from './common.service';
import { RequestTrackingService } from './request-tracking.service';

/** The message the server sends to one user as their background ZIP download progresses. */
const DOWNLOAD_JOB_EVENT = 'DownloadJob';

/** How long to wait before trying to reconnect once the connection has been lost for good. */
const RECONNECT_DELAY_MS = 10000;

@Injectable({
    providedIn: 'root'
})
export class SignalrService {
    private hubConnection!: signalR.HubConnection;
    /** The token the current connection was opened with; the server knows the user from it. */
    private connectedToken = '';
    /** Set while the connection is closed on purpose (a restart for another user, or the app stopping it). */
    private restarting = false;
    private stopRequested = false;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    currentMessage!: NotificationResponseForApprovals;

    private readonly downloadJobSubject = new Subject<DownloadJobStatus>();
    private readonly reconnectedSubject = new Subject<void>();

    /** Progress of the signed-in user's background ZIP download. */
    readonly downloadJob$ = this.downloadJobSubject.asObservable();
    /** Fires after the connection came back; messages sent while it was down are lost, so listeners should re-ask. */
    readonly reconnected$ = this.reconnectedSubject.asObservable();

    constructor(private requestTrackingService: RequestTrackingService, private commonService: CommonService) {
        try {
            this.hubConnection = new signalR.HubConnectionBuilder()
                .withUrl('notification-hub', { accessTokenFactory: () => this.readToken() })
                .withAutomaticReconnect()
                .build();
            this.listenForMessages();
            this.hubConnection.onreconnected(() => this.reconnectedSubject.next());
            // The automatic reconnect gives up after a few attempts, and never retries a first connection that failed.
            // Without this a ready ZIP would never be heard of again until the user navigated.
            this.hubConnection.onclose(() => this.scheduleReconnect());
        } catch (e) {
            console.log(e)
        }
    }

    startConnection(): void {
        if (!this.hubConnection || this.hubConnection.state !== signalR.HubConnectionState.Disconnected) return;

        this.stopRequested = false;
        this.connectedToken = this.readToken();
        this.hubConnection
            .start()
            .then(() => console.log('SignalR connected.'))
            .catch((err) => {
                console.error('SignalR connection error:', err);
                this.scheduleReconnect();
            });
    }

    /**
     * Makes sure the connection belongs to the signed-in user. The connection may have been opened before sign-in,
     * with no token; the server then cannot address messages to this user, so it is opened again.
     */
    async ensureUserConnection(): Promise<void> {
        if (!this.hubConnection) return;

        const token = this.readToken();
        if (this.hubConnection.state === signalR.HubConnectionState.Connected && token === this.connectedToken) return;

        this.stopRequested = false;
        try {
            if (this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
                // Closing it here is on purpose: the close handler must not start a second reconnect on top of this one.
                this.restarting = true;
                await this.hubConnection.stop();
            }
            this.connectedToken = token;
            await this.hubConnection.start();
        } catch (err) {
            console.error('SignalR connection error:', err);
            this.scheduleReconnect();
        } finally {
            this.restarting = false;
        }
    }

    /** Tries to get the connection back every few seconds for as long as the page is open, then tells listeners to catch up. */
    private scheduleReconnect(): void {
        if (this.stopRequested || this.restarting || this.reconnectTimer !== null) return;

        this.reconnectTimer = setTimeout(async () => {
            this.reconnectTimer = null;
            await this.ensureUserConnection();
            if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
                this.reconnectedSubject.next();
            }
        }, RECONNECT_DELAY_MS);
    }

    private listenForMessages(): void {
        // Handlers are registered once, here, so reconnecting can never make a message arrive twice.
        this.hubConnection.on('Approvals', (message: NotificationResponseForApprovals) => {
            const { requestType, uniqueIdInDatabase, isLocked } = message;
            this.requestTrackingService.updateList(requestType, uniqueIdInDatabase, isLocked);
        });
        this.hubConnection.on(DOWNLOAD_JOB_EVENT, (status: DownloadJobStatus) => this.downloadJobSubject.next(status));
    }

    private readToken(): string {
        const encrypted = localStorage.getItem('token');
        return encrypted ? this.commonService.decrypt(encrypted) : '';
    }

    stopConnection(): void {
        this.stopRequested = true;
        this.hubConnection
            .stop()
            .then(() => console.log('SignalR connection stopped.'))
            .catch((err) => console.error('Error stopping SignalR connection:', err));
    }

}
